import { getCurrentScope, onScopeDispose, ref, watch, type Ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import DataLayer from '@services/data-layer/index'
import extractGenres from '@utils/extract-genres'
import { useGenresStore } from '@state/stores/genres'

export type TypeaheadKind = 'mix' | 'artist' | 'show' | 'genre' | 'playlist' | 'chart'

export interface TypeaheadRow {
    kind: TypeaheadKind
    id: string
    title: string
    typeLine: string
    image: Record<string, unknown> | null
    imageType: 'album_art' | 'photo' | 'artwork'
    to: RouteLocationRaw
}

const PAGE = 8
const MAX_ROWS = 6

type AnyRecord = Record<string, any>

interface TypeaheadGenre {
    name: string
    slug: string
    // The store's genre, carrying the `artwork` the row renders — null unless the store
    // knows the name AND has artwork with a url (then the row falls back to its glyph
    // tile, rather than an empty gradient square)
    resource: AnyRecord | null
}

// The API sends `mix.genre` as genre NAMES. Resolve each through the genres store and fall
// back to the name as the slug, like genres-list: Router 4 throws on an undefined `slug`.
// Objects that already carry a name are tolerated. De-duplicated by slug.
function normaliseGenres(raw: unknown[], known: AnyRecord[]): TypeaheadGenre[] {
    const bySlug = new Map<string, TypeaheadGenre>()
    raw.forEach(entry => {
        const name = typeof entry === 'string' ? entry : (entry as AnyRecord)?.name
        if (!name) return
        const stored = known.find(candidate => candidate.name === name) || null
        const match = typeof entry === 'string' ? stored : (entry as AnyRecord)
        const slug = String(match?.slug || name)
        if (!bySlug.has(slug)) bySlug.set(slug, { name: String(name), slug, resource: stored?.artwork?.url ? stored : null })
    })
    return [...bySlug.values()]
}

const toRow = {
    mix: (mix: AnyRecord): TypeaheadRow => ({
        kind: 'mix',
        id: String(mix.id),
        title: mix.title,
        typeLine: `${mix.artist ? mix.artist.title + ' · ' : ''}Mix`,
        image: mix.episode || mix,
        imageType: 'album_art',
        to: { name: 'mix', params: { id: mix.id } },
    }),
    artist: (artist: AnyRecord): TypeaheadRow => ({
        kind: 'artist',
        id: String(artist.id),
        title: artist.title,
        typeLine: 'Artist',
        image: artist,
        imageType: 'photo',
        to: { name: 'artist', params: { artistUrl: artist.url, id: artist.id } },
    }),
    show: (show: AnyRecord): TypeaheadRow => ({
        kind: 'show',
        id: String(show.id),
        title: show.title,
        typeLine: `${show.artist ? show.artist.title + ' · ' : ''}Show`,
        image: show,
        imageType: 'album_art',
        to: { name: 'show', params: { id: show.id } },
    }),
    genre: (genre: TypeaheadGenre): TypeaheadRow => ({
        kind: 'genre',
        id: genre.slug,
        title: genre.name,
        typeLine: 'Genre',
        image: genre.resource,
        imageType: 'artwork',
        to: { name: 'genre', params: { slug: genre.slug } },
    }),
    playlist: (playlist: AnyRecord): TypeaheadRow => {
        const isChart = playlist.type === 'chart'
        return {
            kind: isChart ? 'chart' : 'playlist',
            id: String(playlist.id),
            title: playlist.title,
            typeLine: isChart ? 'Chart' : 'Playlist',
            image: playlist.revisions_album_art ? { album_art: playlist.revisions_album_art } : null,
            imageType: 'album_art',
            to: { name: isChart ? 'browse/charts' : 'browse/playlists', params: { id: playlist.id } },
        }
    },
}

const key = (row: TypeaheadRow) => `${row.kind}:${row.id}`

// Best match first (API X-Search-Best-Match), then 2 mixes · 2 artists · 1 show ·
// 1 genre (· 1 playlist · 1 chart when the index sends them — R4), filled to six
// from the remaining mixes. Never the same entity twice.
function buildRows(body: AnyRecord, bestMatchHeader: string, knownGenres: AnyRecord[]): { bestMatch: TypeaheadRow | null; rows: TypeaheadRow[] } {
    const mixes: AnyRecord[] = body.Mixes || []
    const artists: AnyRecord[] = body.Artists || []
    const shows: AnyRecord[] = body.Shows || []
    const playlists: AnyRecord[] = body.Playlists || []
    const genres = normaliseGenres(extractGenres(mixes), knownGenres)

    let bestMatch: TypeaheadRow | null = null
    if (bestMatchHeader === 'Mixes' && mixes[0]) bestMatch = toRow.mix(mixes[0])
    else if (bestMatchHeader === 'Artists' && artists[0]) bestMatch = toRow.artist(artists[0])
    else if (bestMatchHeader === 'Shows' && shows[0]) bestMatch = toRow.show(shows[0])
    else if (bestMatchHeader === 'Genres' && genres[0]) bestMatch = toRow.genre(genres[0])

    const taken = new Set(bestMatch ? [key(bestMatch)] : [])
    const rows: TypeaheadRow[] = []
    const push = (candidates: TypeaheadRow[], count: number) => {
        candidates.forEach(row => {
            if (count <= 0 || rows.length >= MAX_ROWS || taken.has(key(row))) return
            taken.add(key(row))
            rows.push(row)
            count--
        })
    }

    push(mixes.map(toRow.mix), 2)
    push(artists.map(toRow.artist), 2)
    push(shows.map(toRow.show), 1)
    push(playlists.filter(p => p.type !== 'chart').map(toRow.playlist), 1)
    push(playlists.filter(p => p.type === 'chart').map(toRow.playlist), 1)
    push(genres.map(toRow.genre), 1)
    push(mixes.map(toRow.mix), MAX_ROWS)

    return { bestMatch, rows }
}

export function useTypeahead(query: Ref<string>, { delay = 300 }: { delay?: number } = {}) {
    const genresStore = useGenresStore()
    const rows = ref<TypeaheadRow[]>([])
    const bestMatch = ref<TypeaheadRow | null>(null)
    const isLoading = ref(false)
    const hasSearched = ref(false)
    let timer: ReturnType<typeof setTimeout> | null = null
    let requestId = 0

    function reset() {
        if (timer) clearTimeout(timer)
        timer = null
        requestId++
        rows.value = []
        bestMatch.value = null
        isLoading.value = false
        hasSearched.value = false
    }

    async function search(keyword: string) {
        const id = ++requestId
        isLoading.value = true
        try {
            const data = await DataLayer.elastic.search(keyword, PAGE)
            if (id !== requestId) return
            const built = buildRows(data?.body || {}, data?.headers?.['x-search-best-match'] || '', genresStore.genres || [])
            bestMatch.value = built.bestMatch
            rows.value = built.rows
            hasSearched.value = true
        } catch (error) {
            if (id !== requestId) return
            console.error('Typeahead search failed:', error)
            rows.value = []
            bestMatch.value = null
            hasSearched.value = true
        } finally {
            if (id === requestId) isLoading.value = false
        }
    }

    watch(query, value => {
        const keyword = (value || '').trim()
        if (timer) clearTimeout(timer)
        if (!keyword) {
            reset()
            return
        }
        // A new keyword retires whatever is in flight: bump the id here so a response
        // for the previous keyword can never commit rows the user has moved past.
        requestId++
        // Results are already showing: flag the pending query now so the panel can hide
        // stale "no match" copy. Before the first search the panel stays closed.
        if (hasSearched.value) isLoading.value = true
        timer = setTimeout(() => search(keyword), delay)
    })

    // A pending debounce must not fire after the host unmounts
    if (getCurrentScope()) onScopeDispose(reset)

    return { rows, bestMatch, isLoading, hasSearched, reset }
}
